import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useState, useCallback, useEffect, useRef } from 'react';

import { SearchBar } from '../components/SearchBar/SearchBar';
import { updateSubscriberLink } from '../utils/updateSubscriberLink';
import { updateQuery, filterRequestAction } from '../redux/actions';
import {
  getGTMFilterDepartureEvent,
  getGTMFilterTripLengthEvent,
  getGTMFilterDateFromEvent,
  getGTMFilterDateToEvent,
} from '../utils/gtmEvents';

import * as Selectors from '../redux/selectors';

const getSearchBarLabel = (selectedDepartures, selectedTripLength, texts) => {
  let depStr = selectedDepartures.length ? selectedDepartures[0].name : '';
  if (selectedDepartures.length > 1) {
    depStr += `+ ${selectedDepartures.length - 1} ${texts.additional.toLowerCase()}`;
  }

  let tripLengthStr = texts.allTripDurations;
  if (selectedTripLength && selectedTripLength.value !== -1) {
    tripLengthStr = selectedTripLength.displayName;
  }

  return [depStr, tripLengthStr].join(', ');
};

const SearchBarContainer = (props) => {
  const {
    availableDates,
    departures,
    dispatch,
    hasInitialized,
    searchIsOpen,
    selectedDepartures,
    selectedFromDate,
    selectedToDate,
    selectedTripLength,
    texts,
    tripLengths,
  } = props;

  const [activePane, setActivePane] = useState(null);

  // To reduce unnecessary calls, using refs to store previous values and only call updateSubscriberLink if any parameter actually changed.
  const prevParamsRef = useRef({
    selectedDepartures,
    selectedFromDate,
    selectedToDate,
    selectedTripLength,
  });
  const subscriberLinkUpdatedInitially = useRef(false);

  useEffect(() => {
    if (!hasInitialized) return;

    const prev = prevParamsRef.current;
    const params = { selectedDepartures, selectedFromDate, selectedToDate, selectedTripLength };
    const hasChanged = Object.keys(params).some(
      (key) => JSON.stringify(prev[key]) !== JSON.stringify(params[key]),
    );

    if (!subscriberLinkUpdatedInitially.current || hasChanged) {
      updateSubscriberLink(params);
      prevParamsRef.current = params;
      subscriberLinkUpdatedInitially.current = true;
    }
  }, [selectedDepartures, selectedFromDate, selectedToDate, selectedTripLength, hasInitialized]);

  const handleClosePane = useCallback(() => {
    setActivePane(null);
  }, []);

  const handleOpenPane = useCallback(
    (pane) => {
      if (pane === activePane) {
        handleClosePane();
      } else {
        setActivePane(pane);
      }
    },
    [activePane, handleClosePane],
  );

  const handleOpenSearchBar = useCallback(() => {
    dispatch(updateQuery({ search: true }, { event: 'Change button' }));
  }, [dispatch]);

  const handleCloseSearchBar = useCallback(() => {
    dispatch(updateQuery({ search: '' }));
  }, [dispatch]);

  const handleDepartureChange = useCallback(
    (value) => {
      dispatch(updateQuery({ departure: value }));
      dispatch(filterRequestAction({ gtm: getGTMFilterDepartureEvent(value) }));
    },
    [dispatch],
  );

  const handleTripLengthChange = useCallback(
    (value) => {
      dispatch(
        updateQuery({ durationFrom: value.minValue || '', durationTo: value.maxValue || '' }),
      );
      dispatch(filterRequestAction({ gtm: getGTMFilterTripLengthEvent(value) }));
    },
    [dispatch],
  );

  const handleFromDateChange = useCallback(
    (value) => {
      const queryObj = {
        dateFrom: value,
      };

      if (selectedToDate && value > selectedToDate.value) {
        queryObj.dateTo = '';
      }

      dispatch(updateQuery(queryObj));
      dispatch(filterRequestAction({ gtm: getGTMFilterDateFromEvent(value) }));
    },
    [dispatch, selectedToDate],
  );

  const handleToDateChange = useCallback(
    (value) => {
      const dateTo =
        selectedFromDate && value < selectedFromDate.value ? selectedFromDate.value : value;

      dispatch(updateQuery({ dateTo }));
      dispatch(filterRequestAction({ gtm: getGTMFilterDateToEvent(value) }));
    },
    [dispatch, selectedFromDate],
  );

  return (
    <SearchBar
      id="lms-search-panel"
      isOpen={searchIsOpen}
      activePane={activePane}
      searchBarLabel={getSearchBarLabel(selectedDepartures, selectedTripLength, texts)}
      texts={texts}
      departures={departures}
      selectedDepartures={selectedDepartures}
      availableDates={availableDates}
      selectedFromDate={selectedFromDate}
      selectedToDate={selectedToDate}
      selectedTripLength={selectedTripLength}
      tripLengths={tripLengths}
      onOptionClick={handleOpenPane}
      onClosePane={handleClosePane}
      onOpenSearchClick={handleOpenSearchBar}
      onSearchClick={handleCloseSearchBar}
      onDepartureChange={handleDepartureChange}
      onFromDateChange={handleFromDateChange}
      onToDateChange={handleToDateChange}
      onTripLengthChange={handleTripLengthChange}
    />
  );
};

SearchBarContainer.propTypes = {
  availableDates: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, display: PropTypes.string }),
  ),
  departures: PropTypes.arrayOf(
    PropTypes.shape({ code: PropTypes.string, name: PropTypes.string }),
  ),
  dispatch: PropTypes.func,
  hasInitialized: PropTypes.bool,
  searchIsOpen: PropTypes.bool,
  selectedDepartures: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
  selectedFromDate: PropTypes.shape({ value: PropTypes.string, display: PropTypes.string }),
  selectedToDate: PropTypes.shape({ value: PropTypes.string, display: PropTypes.string }),
  selectedTripLength: PropTypes.object,
  texts: PropTypes.object,
  tripLengths: PropTypes.array,
};

function mapStateToProps(state) {
  const query = Selectors.getQueryParams(state);

  return {
    hasInitialized: Selectors.hasInitialized(state),
    departures: Selectors.getDepartures(state),
    selectedDepartures: Selectors.getSelectedDepartures(state),
    tripLengths: Selectors.getTripLengths(state),
    selectedTripLength: Selectors.getSelectedTripLength(state),
    searchIsOpen: query.search,
    availableDates: Selectors.getAvailableDates(state),
    selectedFromDate: Selectors.getSelectedFromDate(state),
    selectedToDate: Selectors.getSelectedToDate(state),
  };
}

export default connect(mapStateToProps)(SearchBarContainer);
